/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from "react";
import { SegmentedControl, Text, Textarea, UnstyledButton } from '@/shared/ui';
import type { SegmentedControlItem } from '@/shared/ui';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { cn, tailwindUtils } from "@/shared/ui/utils/tailwind";
import { drawerSubmitButtonClass } from '@/shared/ui/utils/drawer-submit-button';

const CHANNELS = ["whatsapp", "email", "phone"] as const;

const labelClass = cn(
  'text-[length:var(--text-meta)]',
  '[font-weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

const textareaClass = cn(
  "min-h-[120px] resize-y px-[var(--space-3)] py-[var(--space-2)]",
  "text-[length:var(--text-body-sm)] [font-family:var(--font-family)]",
  "border border-[var(--border-subtle)] rounded-(--radius-md)",
  "bg-[var(--surface-secondary)] text-[var(--text-primary)]",
  "placeholder:text-[var(--text-tertiary)]",
  "focus:outline-none focus:border-[var(--seed-base)]",
);

export interface AddInteractionFormLabels {
  title: string;
  channel: string;
  comment: string;
  placeholder: string;
  add: string;
  channels: Record<string, string>;
}

/**
 * Records a contact with the client.
 *
 * A modal rather than a strip inside the list: the channel and the note are a
 * form, and inline it read as neither — three unlabeled icons above an input,
 * permanently occupying the top of the history it was meant to add to.
 *
 * @param props - Dialog state, the submit handler and label copy.
 * @returns The record-interaction form shell.
 */
export function AddInteractionForm({
  open,
  onClose,
  onSubmit,
  labels,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (channel: string, comment: string) => void;
  labels: AddInteractionFormLabels;
}) {
  const [channel, setChannel] = useState<string>("whatsapp");
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!open) return;
    setChannel("whatsapp");
    setComment("");
  }, [open]);

  const canSubmit = comment.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(channel, comment.trim());
    setComment("");
  };

  const items: SegmentedControlItem<string>[] = CHANNELS.map((value) => ({
    value,
    label: labels.channels[value],
    testId: `interaction-channel-${value}`,
  }));

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={labels.title}
      density="compact"
      footer={(
        <UnstyledButton
          type="button"
          data-testid="interaction-submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label={labels.add}
          className={cn(tailwindUtils.buttonBase, drawerSubmitButtonClass)}
        >
          {labels.add}
        </UnstyledButton>
      )}
    >
      <div data-testid="interaction-form" className="flex flex-col gap-(--space-4)">
        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClass}>{labels.channel}</Text>
          <SegmentedControl
            value={channel}
            onChange={setChannel}
            items={items}
            size="md"
            ariaLabel={labels.channel}
            testId="interaction-channel"
          />
        </fieldset>

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" className={labelClass} htmlFor="interaction-comment">
            {labels.comment}
          </Text>
          <Textarea
            id="interaction-comment"
            data-testid="interaction-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={labels.placeholder}
            rows={4}
            autoFocus
            maxLength={FORM_FIELD_LIMITS.comment}
            className={textareaClass}
            // Enter submits, Shift+Enter breaks the line -- the note is
            // usually one sentence typed straight after the call.
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
            }}
          />
        </fieldset>
      </div>
    </AdaptiveFormShell>
  );
}
